import ToktokdocLib from '@saas/lib/toktokdoc-lib/web'
import {
    EMAIL_CHANGED,
    PASSWORD_CHANGED,
    LOGIN_USER_SUCCESS,
    LOGIN_USER,
    LOGOUT_USER,
    SIGNATURE_CLEARED,
    UPDATE_USER_INFO,
    UPDATE_ADDRESS_INFO,
    UPDATE_HEALTH_SYSTEM_INFO,
    UPDATE_HEALTH_ACTIVITY_INFO,
    UPDATE_TECHNICAL_CONFIG,
    UPDATE_BANK_INFO,
} from './types'
import { ELIGIBLE } from '../../utils/constants'
import { globalConfigFetch } from './MainActions'
import { localStorageManager } from '../../utils/LocalStorageManager'

export const emailChanged = (text: any) => ({
    type: EMAIL_CHANGED,
    payload: text,
})

export const passwordChanged = (text: any) => ({
    type: PASSWORD_CHANGED,
    payload: text,
})

const loginUserFail = (dispatch: any) => {
    localStorageManager.clear()
    dispatch({
        type: LOGOUT_USER,
    })
    const hds = window._env_.REACT_APP_HDS_HOST
    const auth = window._env_.REACT_APP_CUR_AUTH_CLBC
    window.location.href = `${hds}/login?redirect=${auth}`
}

const fetchUserBaseData = async (userId: string) => {
    const User = await ToktokdocLib.User.findOne(userId)

    const [TechnicalConfig, Address, BankInfo] = await Promise.all([
        User.getTechnicalConfig(),
        User.getAddress(),
        User.getBankInfos(),
    ])

    const [ExtraHealthSystemInfo, ExtraHealthActivityInfo] = await Promise.all([
        ToktokdocLib.UserHealthSystemInfo.findOne(User.extraHealthSystemInfo.objectId),
        ToktokdocLib.UserHealthActivityInfo.findOne(User.extraHealthActivityInfo.objectId, [
            'qualification',
            'profession',
            'speciality',
            'speMention',
        ]),
    ])

    const [QualificationArray, globalConfigRaw] = await Promise.all([
        ToktokdocLib.Qualification.getAll(),
        ToktokdocLib.GlobalConfig.findAll(),
    ])

    return {
        User,
        TechnicalConfig,
        Address,
        BankInfo,
        ExtraHealthSystemInfo,
        ExtraHealthActivityInfo,
        QualificationArray,
        globalConfig: globalConfigRaw[0].toJSON(),
    }
}

const fetchHealthActivityData = async (ExtraHealthActivityInfo: any) => {
    // Qualification is a Pointer (1-1)
    const Qualification = await ExtraHealthActivityInfo.getQualification()

    // Profession is an Array but only one is managed (1-1)
    const professionsArray = await ExtraHealthActivityInfo.getProfession()
    const Profession = Array.isArray(professionsArray) && professionsArray.length > 0 ? professionsArray[0] : null

    const [AllProfessionArray, AllSpeMention, AllSpeciality] = await Promise.all([
        Qualification ? Qualification.getRelatedProfession() : Promise.resolve([]),
        Profession ? ToktokdocLib.SpeMention.getFromProfession(Profession) : Promise.resolve([]),
        Profession ? ToktokdocLib.Speciality.getFromProfession(Profession) : Promise.resolve([]),
    ])

    return {
        Qualification,
        Profession,
        AllProfessionArray,
        AllSpeMention,
        AllSpeciality,
    }
}

const buildLoginSuccessPayload = (
    User: any,
    TechnicalConfig: any,
    Address: any,
    BankInfo: any,
    ExtraHealthSystemInfo: any,
    ExtraHealthActivityInfo: any,
    QualificationArray: any[],
    globalConfig: any,
    Qualification: any,
    Profession: any,
    AllProfessionArray: any,
    AllSpeMention: any[],
    AllSpeciality: any[],
) => {
    const securedEmail = ExtraHealthActivityInfo?.securedEmail ?? null
    const administrativeEmail = ExtraHealthActivityInfo?.administrativeEmail ?? null
    const speMention = Array.isArray(ExtraHealthActivityInfo?.speMention) ? ExtraHealthActivityInfo.speMention : []

    return {
        user: {
            ...User.toJSON(),
            fullName: User.getFullName(),
            transIsEnabled: TechnicalConfig.isTransEnabled,
            isConformityEnabled: TechnicalConfig.isConformityEnabled,
            privatePhone: User.getPrivatePhone(),
            contactPhone: User.getContactPhone(),
        },
        bankInfo: BankInfo.toJSON(),
        address: Address ? Address.toJSON() : null,
        extraHealthSystemInfo: ExtraHealthSystemInfo?.toJSON() ?? null,
        extraHealthActivityInfo: ExtraHealthActivityInfo?.toJSON() ?? null,
        allQualification: QualificationArray.map((x: any) => x.toJSON()),
        qualification: Qualification ? Qualification.toJSON() : null,
        allProfession: AllProfessionArray.map((x: any) => x.toJSON()),
        profession: Profession ? Profession.toJSON() : null,
        allSpeMention: AllSpeMention.map((x: any) => x.toJSON()),
        technicalConfig: TechnicalConfig,
        globalConfig,
        speMention,
        allSpeciality: AllSpeciality.map((x: any) => x.toJSON()),
        speciality: ExtraHealthActivityInfo.speciality || null,
        securedEmail,
        administrativeEmail,
    }
}

export const loginUserSuccess = async (dispatch: any, pUser: any, getState: any) => {
    try {
        const baseData = await fetchUserBaseData(pUser.id)

        const healthActivityData = await fetchHealthActivityData(baseData.ExtraHealthActivityInfo)

        const payload = buildLoginSuccessPayload(
            baseData.User,
            baseData.TechnicalConfig,
            baseData.Address,
            baseData.BankInfo,
            baseData.ExtraHealthSystemInfo,
            baseData.ExtraHealthActivityInfo,
            baseData.QualificationArray,
            baseData.globalConfig,
            healthActivityData.Qualification,
            healthActivityData.Profession,
            healthActivityData.AllProfessionArray,
            healthActivityData.AllSpeMention,
            healthActivityData.AllSpeciality,
        )

        dispatch(globalConfigFetch())
        dispatch({
            type: LOGIN_USER_SUCCESS,
            payload,
        })
    } catch (error) {
        console.log(error)
    }
}

export const loginUser = (token: any) => (dispatch: any, getState: any) => {
    dispatch({
        type: LOGIN_USER,
    })
    // @todo login with parse without lib for livequery
    localStorageManager.set('sessionToken', token)
    ToktokdocLib.Auth.Login(token)
        .then((User: any) => {
            // set custom headers to have better logs in api-parse
            ToktokdocLib.Parse.CoreManager.set('REQUEST_HEADERS', {
                'X-TTD-USER-ID': User.id,
                'X-TTD-APP': 'DOCTOR',
            })
            loginUserSuccess(dispatch, User, getState)
        })
        .catch((error: any) => {
            console.log('LOGIN USER FAIL : ', error)
            loginUserFail(dispatch)
        })
}

export const hideLoading = () => (dispatch: any) => {
    dispatch({
        type: LOGOUT_USER,
    })
}

export const logoutUser = () => (dispatch: any) => {
    ToktokdocLib.Auth.LogOut()
    dispatch({
        type: LOGOUT_USER,
    })
}

export const clearSignature = () => (dispatch: any) => {
    dispatch({
        type: SIGNATURE_CLEARED,
    })
}

/**
 * @description Update the signature content in local redux store
 * @param {String} [signContent=0] Signuture image coded in base64
 * @returns {void}
 */
export const updateSignature = (signContent: any) => async (dispatch: any) => {
    dispatch({
        type: UPDATE_HEALTH_SYSTEM_INFO,
        payload: {
            signature: signContent,
        },
    })
}

export const updateUserInfo =
    (pInfoKey: any, pUserObject: any, pVal: any, pPersistToDb: any) => async (dispatch: any) => {
        try {
            let User
            let Address
            let ExtraHealthSystemInfo
            let ExtraHealthActivityInfo
            let BankInfos
            let PersistedObject
            let persisted
            switch (pInfoKey) {
                case 'firstName':
                    // pUserObject === user
                    User = ToktokdocLib.User.instance(pUserObject.objectId)
                    PersistedObject = await User.setFirstName(pVal)
                    dispatch({
                        type: UPDATE_USER_INFO,
                        payload: {
                            firstName: PersistedObject.firstName,
                            fullName: PersistedObject.getFullName(),
                        },
                    })
                    break
                case 'lastName':
                    // pUserObject === user
                    User = ToktokdocLib.User.instance(pUserObject.objectId)
                    PersistedObject = await User.setLastName(pVal)
                    dispatch({
                        type: UPDATE_USER_INFO,
                        payload: {
                            lastName: PersistedObject.lastName,
                            fullName: PersistedObject.getFullName(),
                        },
                    })
                    break
                case 'street':
                    // pUserObject === address
                    Address = ToktokdocLib.Address.instance(pUserObject.objectId)
                    PersistedObject = await Address.setStreet(pVal)
                    dispatch({
                        type: UPDATE_ADDRESS_INFO,
                        payload: {
                            street: PersistedObject.street,
                        },
                    })
                    break
                case 'postalCode':
                    // pUserObject === address
                    Address = ToktokdocLib.Address.instance(pUserObject.objectId)
                    PersistedObject = await Address.setPostalCode(pVal)
                    dispatch({
                        type: UPDATE_ADDRESS_INFO,
                        payload: {
                            postalCode: PersistedObject.postalCode,
                        },
                    })
                    break
                case 'city':
                    // pUserObject === address
                    Address = ToktokdocLib.Address.instance(pUserObject.objectId)
                    PersistedObject = await Address.setCity(pVal)
                    dispatch({
                        type: UPDATE_ADDRESS_INFO,
                        payload: {
                            city: PersistedObject.city,
                        },
                    })
                    break
                case 'privatePhone':
                    // pUserObject === user
                    User = ToktokdocLib.User.instance(pUserObject.objectId)
                    PersistedObject = await User.setPrivatePhone(pVal)
                    dispatch({
                        type: UPDATE_USER_INFO,
                        payload: {
                            privatePhone: PersistedObject.privatePhone,
                        },
                    })
                    break
                case 'contactPhone':
                    // pUserObject === User
                    User = ToktokdocLib.User.instance(pUserObject.objectId)
                    await User.setContactPhone(pVal)
                    dispatch({
                        type: UPDATE_USER_INFO,
                        payload: {
                            contactPhone: pVal,
                        },
                    })
                    break
                case 'rppsNum':
                    // pUserObject === UserHealthSystemInfo
                    ExtraHealthSystemInfo = ToktokdocLib.UserHealthSystemInfo.instance(pUserObject.objectId)
                    PersistedObject = await ExtraHealthSystemInfo.setRppsNum(pVal)
                    dispatch({
                        type: UPDATE_HEALTH_SYSTEM_INFO,
                        payload: {
                            rppsNum: PersistedObject.rppsNum,
                        },
                    })
                    break
                case 'technicalConfig': {
                    // pUserObject === user
                    User = ToktokdocLib.User.instance(pUserObject.objectId)
                    const tcFlatObject = await User.getTechnicalConfig()
                    const TechnicalConfig = await ToktokdocLib.UserTechnicalConfig.instance(tcFlatObject.objectId)
                    const hash = pVal.hashCheck
                    const result = pVal.isEligible === ELIGIBLE
                    PersistedObject = await TechnicalConfig.setConformityInfos(hash, result)
                    dispatch({
                        type: UPDATE_TECHNICAL_CONFIG,
                        payload: {
                            value: PersistedObject,
                        },
                    })
                    break
                }
                case 'amNum':
                    // pUserObject === UserHealthSystemInfo
                    ExtraHealthSystemInfo = ToktokdocLib.UserHealthSystemInfo.instance(pUserObject.objectId)
                    PersistedObject = await ExtraHealthSystemInfo.setAmNum(pVal)
                    dispatch({
                        type: UPDATE_HEALTH_SYSTEM_INFO,
                        payload: {
                            amNum: PersistedObject.amNum,
                        },
                    })
                    break
                case 'qualification': {
                    // pUserObject === UserHealthActivityInfo
                    ExtraHealthActivityInfo = ToktokdocLib.UserHealthActivityInfo.instance(pUserObject.objectId)
                    const Qualification = ToktokdocLib.Qualification.instance(pVal.objectId)
                    ExtraHealthActivityInfo = await ExtraHealthActivityInfo.setQualification(Qualification)
                    const AllProfessionArray = await Qualification.getRelatedProfession()
                    dispatch({
                        type: UPDATE_HEALTH_ACTIVITY_INFO,
                        payload: {
                            qualification: Qualification.toJSON(),
                            allProfession: AllProfessionArray.map((x: any) => x.toJSON()),
                            profession: null,
                            allSpeMention: [],
                            speMention: [],
                            allSpeciality: [],
                            speciality: null,
                            extraHealthActivityInfo: ExtraHealthActivityInfo.toJSON(),
                        },
                    })
                    break
                }
                case 'profession': {
                    // pUserObject === UserHealthActivityInfo
                    ExtraHealthActivityInfo = ToktokdocLib.UserHealthActivityInfo.instance(pUserObject.objectId)
                    const Profession = ToktokdocLib.Profession.instance(pVal.objectId)
                    ExtraHealthActivityInfo = await ExtraHealthActivityInfo.setProfession(Profession)
                    const AllSpeMention = await ToktokdocLib.SpeMention.getFromProfession(Profession)
                    const AllSpeciality = await ToktokdocLib.Speciality.getFromProfession(Profession)
                    dispatch({
                        type: UPDATE_HEALTH_ACTIVITY_INFO,
                        payload: {
                            profession: Profession ? Profession.toJSON() : null,
                            allSpeMention: AllSpeMention.map((x: any) => ({
                                ...x.toJSON(),
                            })),
                            speMention: [],
                            allSpeciality: AllSpeciality.map((x: any) => ({
                                ...x.toJSON(),
                            })),
                            speciality: null,
                            extraHealthActivityInfo: ExtraHealthActivityInfo.toJSON(),
                        },
                    })
                    break
                }
                case 'speciality':
                    // pUserObject === UserHealthActivityInfo
                    ExtraHealthActivityInfo = ToktokdocLib.UserHealthActivityInfo.instance(pUserObject.objectId)
                    ExtraHealthActivityInfo = await ExtraHealthActivityInfo.setSpeciality(pVal.objectId)
                    dispatch({
                        type: UPDATE_HEALTH_ACTIVITY_INFO,
                        payload: {
                            speciality: pVal,
                            extraHealthActivityInfo: ExtraHealthActivityInfo.toJSON(),
                        },
                    })
                    break
                case 'speMention':
                    // pUserObject === UserHealthActivityInfo
                    ExtraHealthActivityInfo = ToktokdocLib.UserHealthActivityInfo.instance(pUserObject.objectId)
                    await ExtraHealthActivityInfo.setSpeMention(pVal)
                    dispatch({
                        type: UPDATE_HEALTH_ACTIVITY_INFO,
                        payload: {
                            speMention: pVal,
                        },
                    })
                    break
                case 'isEmployee':
                    // pUserObject === UserHealthSystemInfo
                    ExtraHealthSystemInfo = ToktokdocLib.UserHealthSystemInfo.instance(pUserObject.objectId)
                    PersistedObject = await ExtraHealthSystemInfo.setIsEmployee(pVal)
                    persisted = PersistedObject.toJSON()
                    dispatch({
                        type: UPDATE_HEALTH_SYSTEM_INFO,
                        payload: {
                            isEmployee: persisted.isEmployee,
                            isSectorOne: persisted.isSectorOne,
                            isOptam: persisted.isOptam,
                        },
                    })
                    break
                case 'isSectorOne':
                    // pUserObject === UserHealthSystemInfo
                    ExtraHealthSystemInfo = ToktokdocLib.UserHealthSystemInfo.instance(pUserObject.objectId)
                    PersistedObject = await ExtraHealthSystemInfo.setIsSectorOne(pVal)
                    persisted = PersistedObject.toJSON()
                    dispatch({
                        type: UPDATE_HEALTH_SYSTEM_INFO,
                        payload: {
                            isSectorOne: persisted.isSectorOne,
                        },
                    })
                    break
                case 'employedSocName':
                    // pUserObject === UserHealthSystemInfo
                    ExtraHealthSystemInfo = ToktokdocLib.UserHealthSystemInfo.instance(pUserObject.objectId)
                    PersistedObject = await ExtraHealthSystemInfo.setEmployedSocName(pVal)
                    persisted = PersistedObject.toJSON()
                    dispatch({
                        type: UPDATE_HEALTH_SYSTEM_INFO,
                        payload: {
                            employedSocName: persisted.employedSocName,
                        },
                    })
                    break
                case 'isOptam':
                    // pUserObject === UserHealthSystemInfo
                    ExtraHealthSystemInfo = ToktokdocLib.UserHealthSystemInfo.instance(pUserObject.objectId)
                    PersistedObject = await ExtraHealthSystemInfo.setIsOptam(pVal)
                    persisted = PersistedObject.toJSON()
                    dispatch({
                        type: UPDATE_HEALTH_SYSTEM_INFO,
                        payload: {
                            isOptam: persisted.isOptam,
                        },
                    })
                    break
                case 'bic':
                    // pUserObject === BankInfos
                    BankInfos = ToktokdocLib.BankInfos.instance(pUserObject.objectId)
                    if (pPersistToDb) await BankInfos.setBic(pVal)
                    dispatch({
                        type: UPDATE_BANK_INFO,
                        payload: {
                            bic: pVal,
                        },
                    })
                    break
                case 'iban':
                    // pUserObject === BankInfos
                    BankInfos = ToktokdocLib.BankInfos.instance(pUserObject.objectId)
                    if (pPersistToDb) await BankInfos.setIban(pVal)
                    dispatch({
                        type: UPDATE_BANK_INFO,
                        payload: {
                            iban: pVal,
                        },
                    })
                    break
                case 'bankName':
                    // pUserObject === BankInfos
                    BankInfos = ToktokdocLib.BankInfos.instance(pUserObject.objectId)
                    PersistedObject = await BankInfos.setBankName(pVal)
                    dispatch({
                        type: UPDATE_BANK_INFO,
                        payload: {
                            bankName: PersistedObject.bankName,
                        },
                    })
                    break
                case 'lastCGUChecked': {
                    User = ToktokdocLib.User.instance(pUserObject.objectId)
                    const rawTechnicalConfig = await User.getTechnicalConfig()
                    const technicalConfig = await ToktokdocLib.UserTechnicalConfig.instance(rawTechnicalConfig.objectId)
                    const now = new Date()
                    PersistedObject = await technicalConfig.setLastCGUChecked(now)
                    dispatch({
                        type: UPDATE_TECHNICAL_CONFIG,
                        payload: {
                            value: PersistedObject,
                        },
                    })
                    break
                }
                case 'securedEmail':
                    // pUserObject === UserHealthActivityInfo
                    ExtraHealthActivityInfo = ToktokdocLib.UserHealthActivityInfo.instance(pUserObject.objectId)
                    if (pPersistToDb) ExtraHealthActivityInfo = await ExtraHealthActivityInfo.setSecuredEmail(pVal)
                    dispatch({
                        type: UPDATE_HEALTH_ACTIVITY_INFO,
                        payload: {
                            securedEmail: pVal,
                        },
                    })
                    break
                case 'administrativeEmail':
                    ExtraHealthActivityInfo = ToktokdocLib.UserHealthActivityInfo.instance(pUserObject.objectId)
                    if (pPersistToDb)
                        ExtraHealthActivityInfo = await ExtraHealthActivityInfo.setAdministrativeEmail(pVal)
                    dispatch({
                        type: UPDATE_HEALTH_ACTIVITY_INFO,
                        payload: {
                            administrativeEmail: pVal,
                        },
                    })
                    break
                default:
                    break
            }
        } catch (error) {
            console.log(error)
        }
    }
